import { Controller } from "@hotwired/stimulus"

interface OpenDetail {
  collectableSgid?: string
  collectableName?: string
}

// The shared "New collection" modal (one instance per page). Triggers
// dispatch a document-level new-collection:open event — with collectable
// context from the Save-to menu, without it from the library panel CTA.
export default class extends Controller<HTMLElement & { open: boolean }> {
  static targets = [
    "name",
    "submit",
    "addRow",
    "addCheckbox",
    "collectableName",
    "collectableSgid",
    "visibilityInput",
    "visibilityLabel",
    "visibilityIcon",
  ]

  declare readonly nameTarget: HTMLInputElement
  declare readonly submitTarget: HTMLButtonElement
  declare readonly addRowTarget: HTMLElement
  declare readonly addCheckboxTarget: HTMLInputElement
  declare readonly collectableNameTarget: HTMLElement
  declare readonly collectableSgidTarget: HTMLInputElement
  declare readonly visibilityInputTarget: HTMLInputElement
  declare readonly visibilityLabelTarget: HTMLElement
  declare readonly hasVisibilityLabelTarget: boolean
  declare readonly visibilityIconTarget: HTMLElement
  declare readonly hasVisibilityIconTarget: boolean

  open(event: CustomEvent) {
    const detail = (event.detail ?? {}) as OpenDetail

    this.nameTarget.value = ""
    this.submitTarget.disabled = true
    this.setVisibility("personal", "Private", "lock")

    if (detail.collectableSgid) {
      this.collectableNameTarget.textContent = detail.collectableName ?? ""
      this.collectableSgidTarget.value = detail.collectableSgid
      this.addCheckboxTarget.checked = true
      this.syncCollectableFields()
      this.addRowTarget.classList.remove("hidden")
    } else {
      this.addRowTarget.classList.add("hidden")
      this.collectableSgidTarget.disabled = true
    }

    this.element.open = true
    // Focus after wa-dialog finishes showing (its own autofocus handling
    // targets the initial-focus slot, which we don't use).
    requestAnimationFrame(() => this.nameTarget.focus())
  }

  close() {
    this.element.open = false
  }

  nameChanged() {
    this.submitTarget.disabled = this.nameTarget.value.trim() === ""
  }

  // Unchecking "Add X to this collection" disables the hidden collectable
  // fields so they don't submit.
  syncCollectableFields() {
    this.collectableSgidTarget.disabled = !this.addCheckboxTarget.checked
  }

  // Same pill + menu control as the detail page, acting as a form input.
  chooseVisibility(event: Event) {
    const params = (
      event as Event & {
        params: { value: string; label: string; icon: string }
      }
    ).params
    this.setVisibility(params.value, params.label, params.icon)
    document.getElementById("new_collection_visibility_menu")?.hidePopover?.()
  }

  // Label/icon only exist while the public-collections flag renders the
  // visibility pill; flag-off modals still reset the hidden input.
  private setVisibility(value: string, label: string, icon: string) {
    this.visibilityInputTarget.value = value
    if (!this.hasVisibilityLabelTarget || !this.hasVisibilityIconTarget) return
    this.visibilityLabelTarget.textContent = label
    this.visibilityIconTarget.setAttribute("name", icon)
  }

  submitEnd(event: CustomEvent) {
    const detail = event.detail as { success?: boolean } | null
    if (detail?.success !== false) this.close()
  }
}
